/**
 * When a scheduled backup runs, in the reader's time and in the server's.
 *
 * **The backend stores every part of a schedule in UTC** -- the minute, the
 * hour, the day of the week and the day of the month -- and says in the model
 * that converting is the frontend's job. So a schedule somebody sets for three
 * in the morning is stored as whatever three in the morning is in Greenwich,
 * and reading the stored values back as though they were local would move every
 * schedule by the reader's own offset.
 *
 * One set of values is held, not two. Upstream keeps the local values and the
 * UTC values side by side and syncs them on every change, which is two sources
 * of truth for one schedule and a `syncedFromValues` flag to arbitrate. Here the
 * form holds what somebody typed and these functions answer the other side of
 * it, so there is nothing to keep in step.
 *
 * The timezone is a parameter rather than the ambient one, which is what makes
 * any of this testable: the suite runs pinned to UTC, where a conversion that
 * does nothing at all and a conversion that is correct are the same function.
 *
 * **Two things this cannot fix, both inherent to storing a day in UTC.** A
 * schedule is a fixed UTC instant, so a clock change moves it by an hour in
 * local terms. And a monthly schedule near midnight can store a day at the
 * other end of the month -- the 1st at 00:30 in Berlin is the 31st at 22:30 in
 * UTC -- which the backend clamps to the last day of a month that has no 31st.
 * `nextRunAt` from the server is the answer that is always right, which is why
 * the form shows it.
 */
import { TZDate } from '@date-fns/tz'

export const SNAPSHOT_INTERVALS = ['HOUR', 'DAY', 'WEEK', 'MONTH'] as const

export type SnapshotInterval = (typeof SNAPSHOT_INTERVALS)[number]

/**
 * Which parts of a schedule an interval reads, which is the backend's own list.
 *
 * The rest are still stored -- the endpoint replaces the whole schedule -- but
 * asking for them would be asking a question the answer to changes nothing:
 * an hourly backup has no hour.
 */
export const SCHEDULE_PARTS = {
  HOUR: ['minute'],
  DAY: ['minute', 'hour'],
  WEEK: ['minute', 'hour', 'dayOfWeek'],
  MONTH: ['minute', 'hour', 'dayOfMonth'],
} as const satisfies Record<SnapshotInterval, readonly string[]>

export type SchedulePart = (typeof SCHEDULE_PARTS)[SnapshotInterval][number]

export function scheduleUses(interval: SnapshotInterval, part: SchedulePart): boolean {
  return (SCHEDULE_PARTS[interval] as readonly string[]).includes(part)
}

/** What somebody typed, in their own time. Monday is 0, as it is on the wire. */
export interface LocalSchedule {
  interval: SnapshotInterval
  minute: number
  hour: number
  dayOfWeek: number
  dayOfMonth: number
}

/** The four time values as the endpoint takes them. */
export interface StoredSchedule {
  minute: number
  hour: number
  day_of_week: number
  day_of_month: number
}

/** Monday-first, from the Sunday-first index `Date` answers with. */
const mondayFirst = (sundayZero: number): number => (sundayZero + 6) % 7

/**
 * The instant a schedule's parts describe, within the reference's own week or
 * month.
 *
 * A concrete instant is needed rather than arithmetic on the offset, because an
 * offset is not a constant: it depends on the date, both for the half-hour and
 * three-quarter-hour zones and for anything observing a clock change.
 */
function instantFor(local: LocalSchedule, timezone: string, reference: Date): TZDate {
  const here = new TZDate(reference, timezone)
  const day = local.interval === 'MONTH' ? local.dayOfMonth : here.getDate()

  const at = new TZDate(
    here.getFullYear(),
    here.getMonth(),
    day,
    local.hour,
    local.minute,
    0,
    0,
    timezone,
  )

  if (local.interval !== 'WEEK') return at

  const shifted = new TZDate(at, timezone)
  shifted.setDate(at.getDate() + (local.dayOfWeek - mondayFirst(at.getDay())))
  return shifted
}

/** What to send for a schedule somebody described in their own time. */
export function toStored(
  local: LocalSchedule,
  timezone: string,
  reference: Date,
): StoredSchedule {
  const at = new Date(instantFor(local, timezone, reference).getTime())

  return {
    minute: at.getUTCMinutes(),
    hour: at.getUTCHours(),
    day_of_week: mondayFirst(at.getUTCDay()),
    day_of_month: at.getUTCDate(),
  }
}

/** What a stored schedule reads as in the reader's own time. */
export function toLocal(
  stored: StoredSchedule,
  interval: SnapshotInterval,
  timezone: string,
  reference: Date,
): LocalSchedule {
  const at = new Date(reference)
  at.setUTCHours(stored.hour, stored.minute, 0, 0)

  if (interval === 'MONTH') {
    at.setUTCDate(stored.day_of_month)
  } else if (interval === 'WEEK') {
    at.setUTCDate(at.getUTCDate() + (stored.day_of_week - mondayFirst(at.getUTCDay())))
  }

  const here = new TZDate(at, timezone)
  return {
    interval,
    minute: here.getMinutes(),
    hour: here.getHours(),
    dayOfWeek: mondayFirst(here.getDay()),
    dayOfMonth: here.getDate(),
  }
}

/** The schedule a database has none of yet: nightly, keeping the last week. */
export function defaultSchedule(): LocalSchedule {
  return { interval: 'DAY', minute: 0, hour: 3, dayOfWeek: 0, dayOfMonth: 1 }
}

export const DEFAULT_KEEP_LAST = 7
